/**
 * Componente de Dirección Asistida con Geocodificación
 * 
 * Flujo:
 * 1. Seleccionar Partido/Municipio
 * 2. Seleccionar Localidad (para organizar usuarios - NO afecta geocodificación excepto en "Otro")
 * 3. Escribir Calle (con autocompletado)
 * 4. Escribir Número
 * 5. Geocodificación automática:
 *    - Municipios normales: busca por partido + calle (ignora localidad)
 *    - Municipio "Otro": busca por localidad (texto libre) + calle en toda la provincia
 * 6. Opción de ajustar pin manualmente
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPin, Search, Loader2, CheckCircle, AlertCircle, Navigation, LocateFixed } from 'lucide-react';
import { MUNICIPIOS, tieneLocalidadesPredefinidas, getLocalidades } from '../utils/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;

// Pin SVG para el marcador (equivalente a markerIcon de Leaflet).
function MapMarkerPin() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="28" height="42"
      style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.35))' }}>
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"
        fill="#2563EB" stroke="white" strokeWidth="1.2" />
      <circle cx="12" cy="12" r="5" fill="white" />
    </svg>
  );
}

// Centros de municipios para centrar el mapa
const CENTROS_MUNICIPIOS = {
  'Lomas de Zamora': { lat: -34.7608, lng: -58.4092 },
  'Almirante Brown': { lat: -34.8177, lng: -58.3967 },
  'Avellaneda': { lat: -34.6621, lng: -58.3669 },
  'Lanús': { lat: -34.7060, lng: -58.3925 },
  'Esteban Echeverría': { lat: -34.8167, lng: -58.4667 },
  'Ezeiza': { lat: -34.8500, lng: -58.5167 },
  'CABA': { lat: -34.6037, lng: -58.3816 }
};

// Controlador del mapa Google: pan + zoom cuando cambian coordenadas.
function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !center) return;
    map.panTo({ lat: center.lat, lng: center.lng });
    if (zoom) map.setZoom(zoom);
  }, [center, zoom, map]);
  return null;
}

// Handler de clicks sobre el mapa.
function MapClickHandler({ onMapClick }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !onMapClick) return;
    const listener = map.addListener('click', (e) => {
      const latLng = e.latLng;
      if (!latLng) return;
      onMapClick({ lat: latLng.lat(), lng: latLng.lng() });
    });
    return () => window.google?.maps?.event?.removeListener(listener);
  }, [map, onMapClick]);
  return null;
}

export default function DireccionAsistida({ 
  value = {},  // { municipio, localidad, calle, numero, lat, lon, direccion_texto, precision, modo_accesible }
  onChange,    // callback cuando cambia cualquier valor
  showMap = true,
  mapHeight = "300px"
}) {
  // Estado local
  const [municipio, setMunicipio] = useState(value.municipio || '');
  const [localidad, setLocalidad] = useState(value.localidad || '');
  const [localidadOtra, setLocalidadOtra] = useState('');
  const [calle, setCalle] = useState(value.calle || '');
  const [numero, setNumero] = useState(value.numero || '');
  const [coordenadas, setCoordenadas] = useState(
    value.lat && value.lon ? { lat: value.lat, lng: value.lon } : null
  );
  const [precision, setPrecision] = useState(value.precision || '');
  const [direccionTexto, setDireccionTexto] = useState(value.direccion_texto || '');
  
  // Modo accesible - para usuarios con dificultades de visión
  const [modoAccesible, setModoAccesible] = useState(value.modo_accesible || false);
  
  // Estado para autocompletado
  const [sugerenciasCalle, setSugerenciasCalle] = useState([]);
  const [buscandoCalles, setBuscandoCalles] = useState(false);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  
  // Estado para geocodificación
  const [geocodificando, setGeocodificando] = useState(false);
  const [errorGeo, setErrorGeo] = useState('');
  const [alternativas, setAlternativas] = useState([]);
  
  // Estado para geolocalización
  const [obteniendoUbicacion, setObteniendoUbicacion] = useState(false);
  
  // Ref para debounce
  const debounceRef = useRef(null);
  const inputCalleRef = useRef(null);

  // Efecto para actualizar el padre cuando cambian los valores
  useEffect(() => {
    if (onChange) {
      const localidadFinal = localidad === 'Otro' ? localidadOtra : localidad;
      
      // En modo accesible, construir dirección de texto sin coordenadas
      const direccionTextoFinal = modoAccesible && !coordenadas
        ? `${calle} ${numero}, ${localidadFinal}, ${municipio}`.trim().replace(/^,\s*|,\s*$/g, '')
        : direccionTexto;
      
      onChange({
        municipio,
        localidad: localidadFinal,
        calle,
        numero,
        lat: coordenadas?.lat || null,
        lon: coordenadas?.lng || null,
        direccion_texto: direccionTextoFinal,
        precision: modoAccesible && !coordenadas ? 'pendiente_geocodificacion' : (precision || (coordenadas ? 'manual' : '')),
        fuente_geocodificacion: modoAccesible && !coordenadas ? 'modo_accesible' : (precision === 'manual' ? 'manual' : 'google'),
        modo_accesible: modoAccesible,
        pendiente_geocodificacion: modoAccesible && !coordenadas
      });
    }
  }, [municipio, localidad, localidadOtra, calle, numero, coordenadas, direccionTexto, precision, modoAccesible]);

  // Resetear campos dependientes cuando cambia el municipio
  const handleMunicipioChange = (nuevoMunicipio) => {
    setMunicipio(nuevoMunicipio);
    setLocalidad('');
    setLocalidadOtra('');
    setCalle('');
    setNumero('');
    setCoordenadas(null);
    setPrecision('');
    setDireccionTexto('');
    setSugerenciasCalle([]);
    setAlternativas([]);
    setErrorGeo('');
  };

  // Resetear campos dependientes cuando cambia la localidad
  const handleLocalidadChange = (nuevaLocalidad) => {
    setLocalidad(nuevaLocalidad);
    setLocalidadOtra('');
    setCalle('');
    setNumero('');
    setCoordenadas(null);
    setPrecision('');
    setDireccionTexto('');
    setSugerenciasCalle([]);
    setAlternativas([]);
    setErrorGeo('');
  };

  // Determinar si es municipio "Otro"
  const esMunicipioOtro = municipio?.toLowerCase() === 'otro';

  // Buscar calles con autocompletado
  // Para municipios normales: busca por partido (ignora localidad)
  // Para "Otro": busca por localidad escrita (si hay) en toda la provincia
  const buscarCalles = useCallback(async (texto) => {
    if (!texto || texto.length < 3 || !municipio) {
      setSugerenciasCalle([]);
      return;
    }

    // Para "Otro", necesitamos al menos una localidad escrita
    const localidadParaGeo = esMunicipioOtro ? localidad : '';
    
    setBuscandoCalles(true);
    try {
      // Construir URL con parámetros opcionales
      let url = `${API_URL}/api/geocoding/buscar-calles?q=${encodeURIComponent(texto)}&municipio=${encodeURIComponent(municipio)}`;
      
      // Solo agregar localidad si es "Otro" y hay texto
      if (esMunicipioOtro && localidad) {
        url += `&localidad=${encodeURIComponent(localidad)}`;
      }
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setSugerenciasCalle(data.sugerencias || []);
        setMostrarSugerencias(true);
      }
    } catch (err) {
      console.error('Error buscando calles:', err);
    } finally {
      setBuscandoCalles(false);
    }
  }, [localidad, municipio, esMunicipioOtro]);

  // Manejar cambio en el campo calle con debounce
  const handleCalleChange = (texto) => {
    setCalle(texto);
    setCoordenadas(null);
    setPrecision('');
    setErrorGeo('');
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      buscarCalles(texto);
    }, 300);
  };

  // Seleccionar una sugerencia de calle
  const seleccionarCalle = (sugerencia) => {
    setCalle(sugerencia.calle);
    setSugerenciasCalle([]);
    setMostrarSugerencias(false);
    
    // Si la sugerencia ya tiene coordenadas, usarlas como base
    if (sugerencia.lat && sugerencia.lon) {
      setCoordenadas({ lat: sugerencia.lat, lng: sugerencia.lon });
      setPrecision('aproximada');
    }
  };

  // Geocodificar la dirección completa
  // Para municipios normales: busca por partido + calle (ignora localidad del desplegable)
  // Para "Otro": busca por localidad (texto libre) + calle en toda la provincia
  const geocodificarDireccion = async () => {
    if (!calle || !numero || !municipio) {
      setErrorGeo('Completá todos los campos para ubicar la dirección');
      return;
    }
    
    // Para municipio "Otro", requerimos localidad escrita
    if (esMunicipioOtro && !localidad) {
      setErrorGeo('Para municipios fuera de la zona, escribí la localidad');
      return;
    }

    setGeocodificando(true);
    setErrorGeo('');
    setAlternativas([]);

    try {
      // Construir URL - para municipios normales NO enviamos localidad
      // Para "Otro" sí enviamos la localidad escrita
      let url = `${API_URL}/api/geocoding/geocodificar?calle=${encodeURIComponent(calle)}&numero=${encodeURIComponent(numero)}&municipio=${encodeURIComponent(municipio)}`;
      
      if (esMunicipioOtro && localidad) {
        url += `&localidad=${encodeURIComponent(localidad)}`;
      }
      
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.lat && data.lon) {
          setCoordenadas({ lat: data.lat, lng: data.lon });
          setDireccionTexto(data.direccion_texto || `${calle} ${numero}, ${esMunicipioOtro && localidad ? localidad : municipio}`);
          setPrecision(data.precision || 'aproximada');
          
          // Solo mostrar alternativas si la precisión NO es exacta y hay alternativas
          if (data.precision !== 'exacta' && data.alternativas && data.alternativas.length > 0) {
            setAlternativas(data.alternativas);
          } else {
            setAlternativas([]); // Limpiar alternativas si la ubicación es exacta
          }
          
          // Solo mostrar mensaje de advertencia si no es exacta
          if (data.mensaje && data.precision !== 'exacta') {
            setErrorGeo(data.mensaje);
          }
        } else {
          setErrorGeo(data.mensaje || 'No se encontró la dirección. Podés marcar la ubicación manualmente en el mapa.');
        }
      } else {
        setErrorGeo('Error al geocodificar. Intentá marcar la ubicación manualmente.');
      }
    } catch (err) {
      console.error('Error geocodificando:', err);
      setErrorGeo('Error de conexión. Podés marcar la ubicación manualmente.');
    } finally {
      setGeocodificando(false);
    }
  };

  // Manejar click en el mapa (ajuste manual)
  const handleMapClick = async (coords) => {
    setCoordenadas(coords);
    setPrecision('manual');
    setErrorGeo('');
    setAlternativas([]); // Limpiar alternativas cuando se ajusta manualmente
    
    // Intentar obtener la dirección de las coordenadas (geocodificación inversa)
    try {
      const response = await fetch(
        `${API_URL}/api/geocoding/geocodificar-inverso?lat=${coords.lat}&lon=${coords.lng}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.direccion_texto) {
          setDireccionTexto(data.direccion_texto);
        }
      }
    } catch (err) {
      console.error('Error en geocodificación inversa:', err);
    }
  };

  // Seleccionar una alternativa
  const seleccionarAlternativa = (alt) => {
    setCoordenadas({ lat: alt.lat, lng: alt.lon });
    setDireccionTexto(alt.direccion || direccionTexto);
    setPrecision('aproximada');
    setAlternativas([]);
  };

  // Usar ubicación actual del dispositivo
  const usarMiUbicacion = () => {
    if (!navigator.geolocation) {
      setErrorGeo('Tu navegador no soporta geolocalización');
      return;
    }

    setObteniendoUbicacion(true);
    setErrorGeo('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        setCoordenadas(coords);
        setPrecision('gps');
        
        // Hacer geocodificación inversa para obtener la dirección
        try {
          const response = await fetch(
            `${API_URL}/api/geocoding/geocodificar-inverso?lat=${coords.lat}&lon=${coords.lng}`
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setDireccionTexto(data.direccion_texto || '');
              // Intentar rellenar campos si tenemos la info
              if (data.calle) setCalle(data.calle);
              if (data.numero) setNumero(data.numero);
              // Buscar municipio coincidente
              const municipioEncontrado = MUNICIPIOS.find(m => 
                data.municipio?.toLowerCase().includes(m.toLowerCase()) ||
                m.toLowerCase().includes(data.municipio?.toLowerCase() || '')
              );
              if (municipioEncontrado) {
                setMunicipio(municipioEncontrado);
                if (data.localidad) setLocalidad(data.localidad);
              }
            }
          }
        } catch (err) {
          console.error('Error en geocodificación inversa:', err);
        }
        
        setObteniendoUbicacion(false);
      },
      (error) => {
        setObteniendoUbicacion(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setErrorGeo('Permiso de ubicación denegado. Habilitalo en la configuración del navegador.');
            break;
          case error.POSITION_UNAVAILABLE:
            setErrorGeo('Ubicación no disponible. Intentá de nuevo.');
            break;
          case error.TIMEOUT:
            setErrorGeo('Tiempo de espera agotado. Intentá de nuevo.');
            break;
          default:
            setErrorGeo('Error al obtener ubicación.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Determinar centro del mapa
  const centroMapa = coordenadas || 
    (municipio && CENTROS_MUNICIPIOS[municipio]) || 
    CENTROS_MUNICIPIOS['Lomas de Zamora'];

  // Localidades disponibles
  const localidadesDisponibles = municipio && tieneLocalidadesPredefinidas(municipio) 
    ? getLocalidades(municipio) 
    : [];

  return (
    <div className="space-y-4">
      {/* Opción de accesibilidad */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={modoAccesible}
            onChange={(e) => setModoAccesible(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-describedby="modo-accesible-desc"
          />
          <div>
            <span className="font-medium text-blue-900">Tengo dificultades de visión</span>
            <p id="modo-accesible-desc" className="text-sm text-blue-700 mt-1">
              Si tenés dificultades de visión y no podés ver el mapa, elegí esta opción.
            </p>
          </div>
        </label>
      </div>

      {/* Fila 1: Municipio y Localidad */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Partido/Municipio *</Label>
          <Select value={municipio} onValueChange={handleMunicipioChange}>
            <SelectTrigger>
              <SelectValue placeholder="Seleccioná el municipio" />
            </SelectTrigger>
            <SelectContent>
              {MUNICIPIOS.map(m => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>
            Localidad {esMunicipioOtro ? '*' : ''}
            {!esMunicipioOtro && <span className="text-xs text-gray-500 ml-1">(organizativo)</span>}
          </Label>
          {esMunicipioOtro ? (
            // Para "Otro": campo de texto libre
            <Input
              value={localidad}
              onChange={(e) => setLocalidad(e.target.value)}
              placeholder="Escribí la localidad donde se ubica"
              className={!localidad && municipio ? "border-yellow-400" : ""}
            />
          ) : localidadesDisponibles.length > 0 ? (
            // Para municipios con localidades predefinidas: desplegable
            <>
              <Select value={localidad} onValueChange={handleLocalidadChange} disabled={!municipio}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccioná la localidad" />
                </SelectTrigger>
                <SelectContent>
                  {localidadesDisponibles.map(l => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {localidad === 'Otro' && (
                <Input
                  value={localidadOtra}
                  onChange={(e) => setLocalidadOtra(e.target.value)}
                  placeholder="Escribí el nombre de la localidad"
                  className="mt-2"
                />
              )}
            </>
          ) : (
            // Para otros municipios sin localidades predefinidas: texto libre
            <Input
              value={localidad}
              onChange={(e) => setLocalidad(e.target.value)}
              placeholder={municipio ? "Escribí la localidad (opcional)" : "Seleccioná primero el municipio"}
              disabled={!municipio}
            />
          )}
          {esMunicipioOtro && (
            <p className="text-xs text-blue-600">
              La localidad se usará para ubicar la dirección en la provincia de Buenos Aires
            </p>
          )}
        </div>
      </div>

      {/* Fila 2: Calle y Número */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2 md:col-span-2 relative">
          <Label>Calle *</Label>
          <div className="relative">
            <Input
              ref={inputCalleRef}
              value={calle}
              onChange={(e) => handleCalleChange(e.target.value)}
              onFocus={() => sugerenciasCalle.length > 0 && setMostrarSugerencias(true)}
              onBlur={() => setTimeout(() => setMostrarSugerencias(false), 200)}
              placeholder={
                !municipio 
                  ? "Seleccioná primero el municipio" 
                  : esMunicipioOtro && !localidad
                    ? "Escribí primero la localidad"
                    : "Escribí el nombre de la calle"
              }
              disabled={!municipio || (esMunicipioOtro && !localidad)}
              className="pr-8"
            />
            {buscandoCalles && (
              <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
            )}
          </div>
          
          {/* Dropdown de sugerencias */}
          {mostrarSugerencias && sugerenciasCalle.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
              {sugerenciasCalle.map((sug, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 border-b last:border-b-0"
                  onMouseDown={() => seleccionarCalle(sug)}
                >
                  <span className="font-medium">{sug.calle}</span>
                  <span className="text-gray-500 text-xs ml-2">
                    {sug.localidad}, {sug.municipio}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Número *</Label>
          <Input
            value={numero}
            onChange={(e) => {
              setNumero(e.target.value);
              setCoordenadas(null);
              setPrecision('');
            }}
            placeholder="Ej: 1234"
            disabled={!calle}
          />
        </div>
      </div>

      {/* Botones de geocodificación - Solo si NO está en modo accesible */}
      {!modoAccesible && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            onClick={geocodificarDireccion}
            disabled={!calle || !numero || geocodificando || obteniendoUbicacion}
            variant={coordenadas ? "outline" : "default"}
            className="gap-2"
          >
            {geocodificando ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Buscando ubicación...
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4" />
                {coordenadas ? 'Volver a buscar' : 'Ubicar en el mapa'}
              </>
            )}
          </Button>

          <Button
            type="button"
            onClick={usarMiUbicacion}
            disabled={obteniendoUbicacion || geocodificando}
            variant="outline"
            className="gap-2"
          >
            {obteniendoUbicacion ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Obteniendo...
              </>
            ) : (
              <>
                <LocateFixed className="w-4 h-4" />
                Usar mi ubicación
              </>
            )}
          </Button>

          {coordenadas && (
            <div className="flex items-center gap-1 text-sm">
              {precision === 'exacta' || precision === 'gps' ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <AlertCircle className="w-4 h-4 text-yellow-600" />
              )}
              <span className={precision === 'exacta' || precision === 'gps' ? 'text-green-600' : 'text-yellow-600'}>
                Ubicación {precision === 'gps' ? 'GPS' : precision === 'manual' ? 'manual' : precision}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Mensaje en modo accesible */}
      {modoAccesible && calle && numero && municipio && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">Dirección registrada correctamente</p>
              <p className="text-sm text-green-700">
                {calle} {numero}, {localidad || ''} {localidad ? ',' : ''} {municipio}
              </p>
              <p className="text-xs text-green-600 mt-1">
                La ubicación en el mapa será completada por el equipo del Colegio.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mensaje de error/ayuda */}
      {errorGeo && !coordenadas && (
        <p className="text-sm text-red-600">
          {errorGeo}
        </p>
      )}

      {/* Resumen de dirección guardada (ANTES del mapa para mejor visibilidad) */}
      {direccionTexto && coordenadas && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <p className="text-sm text-green-800">
              <strong>Ubicación guardada:</strong> {direccionTexto}
            </p>
          </div>
          <p className="text-xs text-green-600 mt-1">
            Coordenadas: {coordenadas.lat.toFixed(6)}, {coordenadas.lng.toFixed(6)}
          </p>
        </div>
      )}

      {/* Alternativas de ubicación - SOLO si NO hay coordenadas guardadas */}
      {alternativas.length > 0 && !coordenadas && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm font-medium text-yellow-800 mb-2">
            ¿Alguna de estas ubicaciones es correcta?
          </p>
          <div className="space-y-1">
            {alternativas.map((alt, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => seleccionarAlternativa(alt)}
                className="w-full text-left text-sm p-2 bg-white rounded border hover:bg-yellow-100"
              >
                {alt.direccion || `Opción ${idx + 1}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mapa - Solo si NO está en modo accesible */}
      {showMap && !modoAccesible && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Ubicación en el mapa
            </Label>
            {!coordenadas && (
              <span className="text-xs text-gray-500">
                Completá la dirección o hacé clic en el mapa
              </span>
            )}
          </div>
          
          <div className="rounded-lg overflow-hidden border" style={{ height: mapHeight }} data-testid="direccion-asistida-mapa">
            {GOOGLE_MAPS_API_KEY ? (
              <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['marker']}>
                <Map
                  defaultCenter={{ lat: centroMapa.lat, lng: centroMapa.lng }}
                  defaultZoom={coordenadas ? 17 : 13}
                  mapId={GOOGLE_MAP_ID}
                  style={{ width: '100%', height: '100%' }}
                  gestureHandling="greedy"
                  disableDefaultUI={false}
                  mapTypeControl={false}
                  streetViewControl={false}
                  fullscreenControl={false}
                  clickableIcons={false}
                >
                  <MapController center={coordenadas || centroMapa} zoom={coordenadas ? 17 : 13} />
                  <MapClickHandler onMapClick={handleMapClick} />
                  {coordenadas && (
                    <AdvancedMarker position={{ lat: coordenadas.lat, lng: coordenadas.lng }}>
                      <MapMarkerPin />
                    </AdvancedMarker>
                  )}
                </Map>
              </APIProvider>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 p-4 text-center">
                Falta configurar <code className="mx-1 bg-gray-100 px-1 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code>.
              </div>
            )}
          </div>
          
          <p className="text-xs text-gray-500">
            💡 Si la ubicación no es exacta, podés ajustar el pin haciendo clic en el mapa.
          </p>
        </div>
      )}
    </div>
  );
}
